import { CheckCircle2, Coffee, Code2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import javaImg from "@/assets/cases/etl-fastextractor-java.png";
import pythonImg from "@/assets/cases/etl-python-script.png";

type Case = {
  stack: string;
  icon: typeof Coffee;
  titulo: string;
  descricao: string;
  destaques: string[];
  imagem: string;
  alt: string;
};

const cases: Case[] = [
  {
    stack: "Java • Spring Boot",
    icon: Coffee,
    titulo: "FastExtractor — ETL corporativo em Java",
    descricao:
      "Plataforma de ETL desenvolvida em Spring Boot para extração, validação e carga incremental de arquivos em Data Warehouse, com upload via SFTP e pasta local.",
    destaques: [
      "Java 8 + Spring Boot 2.5",
      "Upload via SFTP e LocalFolder",
      "Detecção automática de updates por hash",
    ],
    imagem: javaImg,
    alt: "Terminal mostrando execução do FastExtractor em Spring Boot",
  },
  {
    stack: "Python • Pandas",
    icon: Code2,
    titulo: "Pipeline de Vendas em Python",
    descricao:
      "Pipeline ETL em Python para consolidar vendas de múltiplas fontes, aplicar regras de negócio com Pandas e carregar incrementalmente em PostgreSQL.",
    destaques: [
      "Pandas + SQLAlchemy",
      "Logging estruturado e idempotente",
      "Agendamento via cron / Airflow",
    ],
    imagem: pythonImg,
    alt: "Script Python de ETL com pandas e SQLAlchemy",
  },
];

export const EtlCasesSection = () => {
  return (
    <section className="py-20 lg:py-28 bg-slate-800/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full mb-5">
            <span className="text-sm text-blue-300">Cases Reais</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold mb-4 bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
            Projetos de ETL que já entregamos
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto">
            Da extração de arquivos corporativos em Java à orquestração de
            pipelines em Python, construímos soluções de dados sob medida.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {cases.map((c) => {
            const Icon = c.icon;
            return (
              <Card
                key={c.titulo}
                className="group bg-slate-800/50 border-white/10 backdrop-blur-sm hover:border-blue-500/40 hover:shadow-xl hover:shadow-blue-500/10 transition-all duration-300 overflow-hidden"
              >
                <CardContent className="p-0">
                  {/* Mockup de janela */}
                  <div className="bg-slate-900/80 border-b border-white/10">
                    <div className="flex items-center gap-2 px-4 py-3">
                      <div className="flex gap-1.5">
                        <span className="w-3 h-3 rounded-full bg-red-500/80" />
                        <span className="w-3 h-3 rounded-full bg-yellow-500/80" />
                        <span className="w-3 h-3 rounded-full bg-green-500/80" />
                      </div>
                      <span className="text-xs text-slate-400 ml-2 truncate">
                        {c.stack}
                      </span>
                    </div>
                    <div className="bg-black/40 aspect-[16/10] overflow-hidden">
                      <img
                        src={c.imagem}
                        alt={c.alt}
                        loading="lazy"
                        width={1280}
                        height={800}
                        className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-500"
                      />
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-blue-500/10 border border-blue-500/20 rounded-lg flex items-center justify-center shrink-0">
                        <Icon className="h-5 w-5 text-blue-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs uppercase tracking-wider text-blue-300">
                          {c.stack}
                        </p>
                        <h3 className="text-lg font-semibold text-white truncate">
                          {c.titulo}
                        </h3>
                      </div>
                    </div>

                    <p className="text-sm text-slate-300 leading-relaxed mb-5">
                      {c.descricao}
                    </p>

                    <ul className="space-y-2">
                      {c.destaques.map((d) => (
                        <li
                          key={d}
                          className="flex items-start gap-2 text-sm text-slate-300"
                        >
                          <CheckCircle2 className="h-4 w-4 text-blue-400 mt-0.5 shrink-0" />
                          <span>{d}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default EtlCasesSection;